import React, { useEffect, useState } from "react";
import { ReferenceDetailContactInfo } from "../../interfaces/referenceDetaill";
import { batchSendCertifiedLetters } from "../../services/reportService";

interface Props {
  references: ReferenceDetailContactInfo[] | null;
  onClose: () => void;
  onSuccess: () => void;
}

const SendBatchCertifiedLetterModal: React.FC<Props> = ({
  references,
  onClose,
  onSuccess,
}) => {
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [sendLetterIds, setSendLetterIds] = useState<number[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasDifferentMailAddr, setHasDifferentMailAddr] = useState(false);
  const [differentMailAddrIds, setDifferentMailAddrIds] = useState<number[]>(
    [],
  );

  useEffect(() => {
    const fetchReferenceOrderDetails = async () => {
      const batchSendLetterIds =
        references
          ?.filter((ref) => !!ref.certified)
          .map((ref) => ref.referenceDetailID) ?? [];

      setSendLetterIds(batchSendLetterIds);

      const ids =
        references
          ?.filter((r) => r.mailAddrDifferent === true)
          .map((r) => r.referenceDetailID) ?? [];

      setDifferentMailAddrIds(ids);
      setHasDifferentMailAddr(ids.length > 0);
    };

    fetchReferenceOrderDetails();
  }, []);

  const handleBatchSendCertifiedLetters = async () => {
    try {
      setLoadingSpinner(true);
      setErrorMessage(null);

      const response = await batchSendCertifiedLetters(sendLetterIds);

      if (!response) {
        setErrorMessage("Failed to send certified letters (no response).");
        return;
      }

      if (!response.ok) {
        let message = "Failed to bulk send certified letters.";
        try {
          const text = await response.text();
          if (text && text.trim().length > 0) {
            message = text;
          }
        } catch {}
        setErrorMessage(message);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "CertifiedReferenceLetters.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      onClose();
      onSuccess();
    } catch (error) {
      console.error("Error sending letters:", error);
      setErrorMessage("An unexpected error occurred while sending letters.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      {loadingSpinner ? (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          <div className="background-mask" onClick={onClose}></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button className="close-button" onClick={onClose}>
                <i className="bi bi-x-circle"></i>
              </button>
            </div>

            <div className="filter-header">
              <h2>Batch Send Certified Letters</h2>
              <div>
                You are about to batch send certified letters to all references
                with 'CSV' selected. An email will also be sent to the
                candidate.
              </div>
              <div className="error">
                These communications will be marked as 'by mail', and pdfs will
                be downloaded to the browser.
              </div>
              <div className="error">
                This operation may take a while depending on how many
                communications are being processed. Please don't disrupt the
                page while the spinner is up.
              </div>
            </div>
            <div className="filter-actions">
              <button
                className={`smbtn btn-green ${
                  sendLetterIds?.length === 0 ? "disabled" : ""
                }`}
                onClick={handleBatchSendCertifiedLetters}
                disabled={sendLetterIds?.length === 0}
              >
                Send Letters
              </button>
            </div>
            {hasDifferentMailAddr && (
              <div className="error" style={{ flex: "0 0 100%" }}>
                The following reference id's have mailing addresses different
                than physical addresses: {differentMailAddrIds.join(", ")}.
              </div>
            )}
            <br></br>
            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default SendBatchCertifiedLetterModal;
